/**
 * InfoHint — compact "i" control with a portal hover/focus tooltip.
 * Escapes overflow:hidden parents (e.g. Node Inspector) via fixed positioning.
 */
import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { clsx } from 'clsx';

type Props = {
    text: string;
    /** Accessible name for the control (defaults to "More information"). */
    label?: string;
    className?: string;
    /** Icon size class — default matches dense inspector chrome. */
    iconClassName?: string;
};

export const InfoHint: React.FC<Props> = ({
    text,
    label = 'More information',
    className,
    iconClassName = 'w-3.5 h-3.5',
}) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const tipId = useId();
    const wrapRef = useRef<HTMLSpanElement>(null);
    const tipRef = useRef<HTMLSpanElement>(null);

    const positionTip = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const tipH = tipRef.current?.offsetHeight ?? 80;
        const tipW = tipRef.current?.offsetWidth ?? 260;
        const gap = 8;
        const spaceBelow = window.innerHeight - rect.bottom;
        const placeAbove = spaceBelow < tipH + gap && rect.top > spaceBelow;
        let top = placeAbove ? rect.top - tipH - gap : rect.bottom + gap;
        top = Math.min(window.innerHeight - tipH - 8, Math.max(8, top));
        const left = Math.min(
            window.innerWidth - tipW / 2 - 8,
            Math.max(tipW / 2 + 8, rect.left + rect.width / 2),
        );
        setCoords({ top, left });
    }, []);

    const show = useCallback(() => setOpen(true), []);
    const hide = useCallback(() => {
        setOpen(false);
        setCoords(null);
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        positionTip();
        const onScrollOrResize = () => positionTip();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open, positionTip, text]);

    return (
        <span
            ref={wrapRef}
            className={clsx('relative inline-flex shrink-0 align-middle', className)}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            <button
                type="button"
                tabIndex={0}
                aria-label={label}
                aria-describedby={open ? tipId : undefined}
                className={clsx(
                    'inline-flex items-center justify-center rounded-full',
                    'text-muted-foreground/80 hover:text-primary',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    'transition-colors',
                )}
                onClick={(e) => {
                    // Keep parent tab/button clicks from firing when toggling the tip.
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Info className={iconClassName} aria-hidden />
            </button>
            {open &&
                createPortal(
                    <span
                        ref={tipRef}
                        id={tipId}
                        role="tooltip"
                        className="fixed z-[9999] w-64 max-w-[min(16rem,calc(100vw-1rem))] pointer-events-none"
                        style={{
                            top: coords?.top ?? 0,
                            left: coords?.left ?? 0,
                            transform: 'translateX(-50%)',
                            visibility: coords ? 'visible' : 'hidden',
                        }}
                    >
                        <span className="block bg-popover text-popover-foreground text-xs leading-snug p-3 rounded-md shadow-lg border border-border text-left font-normal normal-case tracking-normal">
                            {text}
                        </span>
                    </span>,
                    document.body,
                )}
        </span>
    );
};
